import React, { useState, useEffect } from 'react';
import { RestaurantInfo } from './types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ImagePlus, 
  Wand2, 
  Map, 
  Trash, 
  ChevronDown, 
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Loader2,
  Tag,
  FileText,
  Image,
  AlignLeft,
  CircleDot,
  ClipboardList,
  Users,
  Info,
  Briefcase,
  Utensils,
  Coffee,
  Bookmark,
  MoreHorizontal,
  Copy,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { useToast } from '@/hooks/use-toast';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { normalizeLocationData } from '@/lib/coordinates-utils';

interface RestaurantFormProps {
  restaurant: RestaurantInfo;
  index: number;
  pageId: string;
  updateRestaurant: (pageId: string, restaurantId: string, field: keyof RestaurantInfo, value: any) => void;
  batchUpdateRestaurant?: (restaurantId: string, updates: Partial<RestaurantInfo>) => void;
  deleteRestaurant: (pageId: string, restaurantId: string) => void;
  duplicateRestaurant?: (pageId: string, restaurantId: string) => void;
  moveRestaurantToPage?: (restaurantId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// Custom styled label with orange underline and icon
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-sm font-medium text-gray-700", className)}
    {...props}
  >
    <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

// Update input styles by creating a component wrapper
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

const FormTextarea = ({ className, ...props }: React.ComponentProps<typeof Textarea>) => (
  <Textarea
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

export function RestaurantForm({
  restaurant,
  index,
  pageId,
  updateRestaurant,
  batchUpdateRestaurant,
  deleteRestaurant,
  duplicateRestaurant,
  moveRestaurantToPage,
  isCollapsed,
  onToggleCollapse
}: RestaurantFormProps) {
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [restaurantTitle, setRestaurantTitle] = useState<string>(() => {
    return restaurant.name || `Restaurante #${index + 1}`;
  });
  // Track if location differs from title for sync icon
  const [locationDiffersFromTitle, setLocationDiffersFromTitle] = useState(false);

  // Effect to keep restaurantTitle in sync with restaurant.name
  useEffect(() => {
    // Usado apenas para visualização na UI
    const displayTitle = restaurant.name?.trim() ? restaurant.name : `Restaurante #${index + 1}`;
    setRestaurantTitle(displayTitle);
  }, [restaurant.name, index]);

  // Effect to check if location differs from title
  useEffect(() => {
    setLocationDiffersFromTitle(
      !!restaurant.name && 
      (restaurant.location === '' || 
       restaurant.location === undefined || 
       restaurant.name !== restaurant.location)
    );
  }, [restaurant.name, restaurant.location]);

  // Debug effect to track restaurant changes
  useEffect(() => {
    console.log("[RestaurantForm] Restaurant updated:", {
      id: restaurant.id,
      location: restaurant.location,
      coordinates: restaurant.coordinates
    });
  }, [restaurant.location, restaurant.coordinates]);

  // Function to update a field
  const handleUpdate = (field: keyof RestaurantInfo, value: any) => {
    let safeValue = value;
    
    // Special handling for coordinates to ensure they're properly formatted
    if (field === 'coordinates' && value) {
      const lat = typeof value.lat === 'string' ? parseFloat(value.lat) : Number(value.lat);
      const lng = typeof value.lng === 'string' ? parseFloat(value.lng) : Number(value.lng);
      
      safeValue = {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng
      };
    }
    
    updateRestaurant(pageId, restaurant.id, field, safeValue);
  };

  // Function to handle deleting the restaurant
  const handleDelete = () => {
    deleteRestaurant(pageId, restaurant.id);
  };

  // AI Description enhancement
  const handleProcessDescription = async () => {
    if (!restaurant.description || restaurant.description.trim() === '') {
      toast({
        title: "Descrição vazia",
        description: "Por favor, insira um texto na descrição para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingDescription(true);
    
    try {
      // Extract text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = restaurant.description;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Enviar apenas o texto do usuário, sem prompts adicionais
      // Increase maxTokens to avoid truncation
      const response = await chatCompletion(textContent, 'gpt-4-turbo', 0.7, 1500);
      
      if (response && response.choices && response.choices.length > 0) {
        // Extrair o conteúdo da mensagem da resposta
        const generatedContent = response.choices[0].message.content.trim();
        
        // Check if the response was truncated
        if (response.choices[0].finish_reason === 'length') {
          toast({
            title: "Aviso de truncamento",
            description: "O texto gerado foi truncado devido ao limite de tamanho. Considere dividir o conteúdo em partes menores.",
            variant: "default"
          });
        }
        
        // Atualizar o campo de descrição com o conteúdo extraído
        handleUpdate('description', generatedContent);
        
        toast({
          title: "Descrição processada",
          description: "A descrição foi melhorada com sucesso pela IA.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Erro ao processar descrição:', error);
      toast({
        title: "Erro",
        description: "Houve um erro ao processar a descrição. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingDescription(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Parse currency input
  const parseCurrency = (value: string): number | null => {
    if (!value) return null;
    const numericValue = value.replace(/\D/g, '');
    return numericValue ? Number(numericValue) / 100 : null;
  };

  // Handler para o botão de salvar na biblioteca
  const handleSaveToLibrary = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o clique colapse/expanda o item
    setIsSaveToLibraryOpen(true);
  };

  // Function to sync location with title
  const handleSyncLocationWithTitle = () => {
    if (restaurant.name) {
      handleUpdate('location', restaurant.name);
    }
  };

  // Finalizar edição do título
  const handleFinishTitleEdit = () => {
    setIsEditingTitle(false);
    
    // Se o título não estiver vazio
    if (restaurantTitle.trim() !== '') {
      handleUpdate('name', restaurantTitle.trim());
    } else {
      // Se estiver vazio, defina o campo name como vazio
      handleUpdate('name', '');
      setRestaurantTitle(`Restaurante #${index + 1}`);
    }
  };

  return (
    <>
      <Card className={cn(
        "border shadow-sm transition-all border-l-4 border-l-purple-400",
        isCollapsed && "border-transparent border-l-purple-400"
      )}>
        <CardHeader 
          className={cn(
            "cursor-pointer flex items-center justify-between",
            isCollapsed ? "py-2 px-3" : "py-3 px-4"
          )}
          onClick={onToggleCollapse}
        >
          <div className="flex items-center gap-2 w-full">
            <Utensils className="h-4 w-4 text-purple-500" />
            {isEditingTitle ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={restaurantTitle}
                  onChange={(e) => setRestaurantTitle(e.target.value)}
                  className="h-7 mr-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-transparent"
                  autoFocus
                  onFocus={() => {
                    // Se for apenas o placeholder, limpe o campo para edição
                    if (!restaurant.name || restaurant.name.trim() === '') {
                      setRestaurantTitle('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFinishTitleEdit();
                    }
                  }}
                  onBlur={handleFinishTitleEdit}
                />
              </div>
            ) : (
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingTitle(true);
                  // Se for apenas o placeholder, limpe o campo para edição
                  if (!restaurant.name || restaurant.name.trim() === '') {
                    setRestaurantTitle('');
                  }
                }}
              >
                <span className="font-medium">{restaurantTitle}</span>
              </div>
            )}
            
            {/* Quick info for collapsed view */}
            {isCollapsed && restaurant.date && (
              <div className="flex items-center ml-2 text-sm text-gray-500">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <span className="truncate">
                  {restaurant.date.slice(5).replace('-', '/')}
                  {restaurant.time && ` ${restaurant.time}`}
                </span>
              </div>
            )}
            
            {isCollapsed && restaurant.location && (
              <div className="flex items-center ml-3 text-sm text-gray-500">
                <MapPin className="h-3.5 w-3.5 mr-1" />
                <span className="truncate">{restaurant.location}</span>
              </div>
            )}
            
            <div className="flex items-center ml-auto">
              {/* Botão de Salvar na Biblioteca (bookmark) */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 transition-colors hover:text-amber-500"
                onClick={handleSaveToLibrary}
                title="Salvar na Biblioteca"
              >
                <Bookmark className="h-4 w-4 text-gray-500 hover:text-amber-500" />
              </Button>
              
              {/* Dropdown Menu de Ações */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[180px]">
                  {duplicateRestaurant && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateRestaurant(pageId, restaurant.id);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      <span>Duplicar</span>
                    </DropdownMenuItem>
                  )}
                  
                  {moveRestaurantToPage && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        moveRestaurantToPage(restaurant.id);
                      }}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      <span>Mover</span>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRestaurant(pageId, restaurant.id);
                    }}
                    className="text-red-600"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    <span>Excluir</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </CardHeader>

        {!isCollapsed && (
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Name and Location fields side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`name-${restaurant.id}`}
                    icon={Tag}
                  >
                    Nome do Restaurante
                  </StyledLabel>
                </div>
                <div className="relative w-full">
                  <FormInput
                    id={`name-${restaurant.id}`}
                    value={restaurantTitle}
                    onChange={(e) => setRestaurantTitle(e.target.value)}
                    onFocus={() => {
                      // Se for apenas o placeholder, limpe o campo para edição
                      if (!restaurant.name || restaurant.name.trim() === '') {
                        setRestaurantTitle('');
                      }
                    }}
                    onBlur={handleFinishTitleEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleFinishTitleEdit();
                      }
                    }}
                    placeholder="Nome do restaurante"
                  />
                </div>
              </div>
              
              {/* Location with Google Places integration */}
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`location-${restaurant.id}`}
                    icon={MapPin}
                  >
                    Local
                  </StyledLabel>
                </div>
                <div className="relative">
                  <PlacesAutocompleteV1
                    value={restaurant.location || ''}
                    onChange={(value) => handleUpdate('location', value)}
                    onPlaceSelect={(placeData) => {
                      if (placeData) {
                        console.log("[RestaurantForm] Place selected:", placeData);
                        
                        // Use the normalizeLocationData utility
                        const normalizedPlace = normalizeLocationData(placeData);
                        
                        // Skip if normalization failed
                        if (!normalizedPlace) {
                          console.error('[RestaurantForm] Failed to normalize place data:', placeData);
                          toast({
                            title: "Erro ao processar local",
                            description: "Não foi possível processar os dados do local selecionado.",
                            variant: "destructive"
                          });
                          return;
                        }
                        
                        // Create an updates object with normalized location data
                        const updates: Partial<RestaurantInfo> = {
                          placeId: normalizedPlace.placeId,
                          googleMapsUrl: normalizedPlace.googleMapsUrl,
                          location: normalizedPlace.name,
                          coordinates: normalizedPlace.coordinates,
                          city: normalizedPlace.city,
                          country: normalizedPlace.country
                        };
                        
                        // Use the batch update function if available, otherwise update fields individually
                        if (batchUpdateRestaurant) {
                          batchUpdateRestaurant(restaurant.id, updates);
                          // Show toast only after successful update
                          setTimeout(() => {
                            toast({
                              title: "Local adicionado",
                              description: `${normalizedPlace.name} foi vinculado a este restaurante.`,
                            });
                          }, 300);
                        } else {
                          // Fall back to individual updates
                          Object.entries(updates).forEach(([field, value]) => {
                            const key = field as keyof RestaurantInfo;
                            updateRestaurant(pageId, restaurant.id, key, value);
                          });
                          // Show toast only after successful update
                          setTimeout(() => {
                            toast({
                              title: "Local adicionado",
                              description: `${normalizedPlace.name} foi vinculado a este restaurante.`,
                            });
                          }, 300);
                        }
                        
                        // Remove the immediate toast notification
                      }
                    }}
                    placeholder="Digite um local"
                    className="w-full"
                    showNameInsteadOfAddress={true}
                  />
                  {locationDiffersFromTitle && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-10 top-0 h-10 w-10"
                      onClick={handleSyncLocationWithTitle}
                      title="Sincronizar localização com o título"
                    >
                      <RefreshCw className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Description with rich text editor */}
            <div className="space-y-2 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`description-${restaurant.id}`}
                    icon={AlignLeft}
                  >
                    Descrição
                  </StyledLabel>
                </div>
              </div>
              <RichTextEditor
                value={restaurant.description || ''}
                onChange={(value) => handleUpdate('description', value)}
                placeholder="Descreva o restaurante..."
                onProcessWithAI={handleProcessDescription}
                isProcessing={isProcessingDescription}
              />
            </div>
            
            {/* Date and Time fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`date-${restaurant.id}`}
                    icon={Calendar}
                  >
                    Data
                  </StyledLabel>
                </div>
                <FormInput
                  type="date"
                  id={`date-${restaurant.id}`}
                  value={restaurant.date || ''}
                  onChange={(e) => handleUpdate('date', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`time-${restaurant.id}`}
                    icon={Clock}
                  >
                    Horário
                  </StyledLabel>
                </div>
                <FormInput
                  type="time"
                  id={`time-${restaurant.id}`}
                  value={restaurant.time || ''}
                  onChange={(e) => handleUpdate('time', e.target.value)}
                />
              </div>
            </div>
            
            {/* Duration and Cuisine Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`duration-${restaurant.id}`}
                    icon={Clock}
                  >
                    Duração
                  </StyledLabel>
                </div>
                <FormInput
                  id={`duration-${restaurant.id}`}
                  value={restaurant.duration || ''}
                  onChange={(e) => handleUpdate('duration', e.target.value)}
                  placeholder="Ex: 2 horas"
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`cuisine-type-${restaurant.id}`}
                    icon={Utensils}
                  >
                    Tipo de Cozinha
                  </StyledLabel>
                </div>
                <FormInput
                  id={`cuisine-type-${restaurant.id}`}
                  value={restaurant.cuisineType || ''}
                  onChange={(e) => handleUpdate('cuisineType', e.target.value)}
                  placeholder="Ex: Italiana, Japonesa, Regional..."
                />
              </div>
            </div>
            
            {/* Menu Highlights */}
            <div className="space-y-2 flex flex-col">
              <div className="flex flex-col items-start">
                <StyledLabel
                  htmlFor={`menu-highlights-${restaurant.id}`}
                  icon={Coffee}
                >
                  Destaques do Menu
                </StyledLabel>
              </div>
              <FormTextarea
                id={`menu-highlights-${restaurant.id}`}
                value={restaurant.menuHighlights || ''}
                onChange={(e) => handleUpdate('menuHighlights', e.target.value)}
                placeholder="Pratos, bebidas e especialidades que merecem destaque..."
                rows={3}
              />
            </div>
            
            {/* Notes field */}
            <div className="space-y-2 flex flex-col">
              <div className="flex flex-col items-start">
                <StyledLabel
                  htmlFor={`notes-${restaurant.id}`}
                  icon={FileText}
                >
                  Observações
                </StyledLabel>
              </div>
              <FormTextarea
                id={`notes-${restaurant.id}`}
                value={restaurant.notes || ''}
                onChange={(e) => handleUpdate('notes', e.target.value)}
                placeholder="Observações ou instruções especiais..."
                rows={3}
              />
            </div>
            
            {/* Price section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 flex flex-col">
                  <div className="flex flex-col items-start">
                    <StyledLabel
                      htmlFor={`price-${restaurant.id}`}
                      icon={DollarSign}
                    >
                      Preço por pessoa (aproximado)
                    </StyledLabel>
                  </div>
                  <div className="relative">
                    <DollarSign className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-500" />
                    <FormInput
                      id={`price-${restaurant.id}`}
                      value={restaurant.price !== undefined ? formatCurrency(restaurant.price) : ''}
                      onChange={(e) => {
                        const parsed = parseCurrency(e.target.value);
                        handleUpdate('price', parsed);
                      }}
                      className="pl-8"
                      placeholder="0,00"
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                </div>
                <div className="space-y-2 flex flex-col">
                  <div className="flex flex-col items-start">
                    <StyledLabel
                      htmlFor={`currency-${restaurant.id}`}
                      icon={Tag}
                    >
                      Moeda
                    </StyledLabel>
                  </div>
                  <FormInput
                    id={`currency-${restaurant.id}`}
                    value={restaurant.currency || 'BRL'}
                    onChange={(e) => handleUpdate('currency', e.target.value)}
                    placeholder="BRL"
                  />
                </div>
              </div>
            </div>
            
            {/* Image management */}
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    className="text-sm font-medium"
                    icon={Image}
                  >
                    Imagens
                  </StyledLabel>
                </div>
                <div className="flex items-center gap-2">
                  {restaurant.images && restaurant.images.length > 0 ? (
                    <div className="flex items-center gap-1">
                      <div 
                        className="h-8 w-8 rounded-md overflow-hidden border border-gray-200 shadow-sm hover:border-purple-500 cursor-pointer transition-all hover:scale-105"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsImageManagerOpen(true);
                        }}
                      >
                        <img 
                          src={restaurant.images[0]} 
                          alt="Miniatura" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {restaurant.images.length > 1 && (
                        <span 
                          className="text-xs text-gray-500 cursor-pointer hover:text-purple-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsImageManagerOpen(true);
                          }}
                        >
                          +{restaurant.images.length - 1}
                        </span>
                      )}
                    </div>
                  ) : null}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsImageManagerOpen(true);
                    }}
                    className="h-8 bg-white border-gray-200 hover:bg-gray-50 text-purple-500"
                  >
                    <ImagePlus className="h-4 w-4 mr-2" />
                    <span>Gerenciar imagens</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Image Manager Modal */}
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={restaurant.images || []}
          onImagesChange={(images) => {
            handleUpdate('images', images);
          }}
          contextId={restaurant.id}
          contextType="customSection"
        />
      )}
      
      {/* Modal de Salvar na Biblioteca */}
      {isSaveToLibraryOpen && (
        <SaveToLibraryModal
          isOpen={isSaveToLibraryOpen}
          onClose={() => setIsSaveToLibraryOpen(false)}
          item={restaurant}
          itemType="restaurant"
          onSave={(savedItem) => {
            toast({
              title: "Item salvo na biblioteca",
              description: "O restaurante foi salvo com sucesso na sua biblioteca.",
            });
            setIsSaveToLibraryOpen(false);
          }}
        />
      )}
    </>
  );
} 